/* eslint-disable no-tabs */
/**
 * All icons from package 3p-teacheradmin-assets.
 * This is based on a require context match on /icon_.*.\.svg/.
 */
import { assert } from '@test-helpers';
import { isInJest } from '@utils';

export type TeacherAdminIcons =
    | 'message'
    | 'activities-none'
    | 'activities-sadface'
    | 'activities'
    | 'activity_points'
    | 'arrow_down'
    | 'arrow_up'
    | 'asc_letters'
    | 'asc_numbers'
    | 'assessments'
    | 'assign'
    | 'attempts'
    | 'audio'
    | 'back'
    | 'bug'
    | 'calendar'
    | 'camera'
    | 'captions'
    | 'certificates'
    | 'classes'
    | 'clock-timeonline'
    | 'clock'
    | 'clock_4'
    | 'compare'
    | 'contract'
    | 'copy'
    | 'courses'
    | 'cross'
    | 'cross_red_circle'
    | 'curriculum'
    | 'dashboard'
    | 'data'
    | 'delete'
    | 'demo'
    | 'desc_letters'
    | 'desc_numbers'
    | 'down'
    | 'download'
    | 'dropdown'
    | 'early_numeracy'
    | 'edit-course'
    | 'edit'
    | 'empty'
    | 'exit'
    | 'extend_results'
    | 'feedback'
    | 'filter'
    | 'filter_outline'
    | 'forward'
    | 'global_settings'
    | 'grid'
    | 'guide'
    | 'heart'
    | 'help'
    | 'hidden'
    | 'hide_show'
    | 'home'
    | 'image'
    | 'improvement'
    | 'information'
    | 'inprogress'
    | 'interactive'
    | 'left_solid'
    | 'letterSoundAnimation'
    | 'library'
    | 'list'
    | 'livemaths_points'
    | 'loading'
    | 'lock'
    | 'locked'
    | 'locked_color'
    | 'marking'
    | 'menu'
    | 'miniReaders'
    | 'more'
    | 'mouth'
    | 'move-vertical'
    | 'move'
    | 'results_new_courses'
    | 'no_diving'
    | 'notification-bell'
    | 'notifications-solid'
    | 'notifications'
    | 'numbeanie'
    | 'pause'
    | 'pdf'
    | 'plus'
	| 'present'
    | 'preview'
    | 'print-signin'
    | 'print'
    | 'profile'
    | 'psr'
    | 'quickview'
    | 'readers'
    | 'remove'
    | 'reports'
    | 'resources'
    | 'resources_beta'
    | 'results-activity'
    | 'results-assessment'
    | 'results-student'
    | 'results'
    | 'right_solid'
    | 'rollover'
    | 'school'
    | 'search'
    | 'settings'
    | 'signin-card'
    | 'song'
    | 'sort'
    | 'sort_asc'
    | 'sort_desc'
    | 'spine'
    | 'star'
    | 'student-management'
    | 'student_reports'
    | 'students'
    | 'teach'
    | 'teach_notes'
    | 'teacher'
    | 'tick'
    | 'toolkit'
    | 'unlock'
    | 'unlocked'
    | 'unlocked_color'
    | 'up'
    | 'up_solid'
    | 'upf'
    | 'upf_beta'
    | 'upf_beta_v1'
    | 'upf_beta_v2'
    | 'upload'
    | 'view'
    | 'visible'
    | 'warning'
    | 'whole-class'
    | 'world'
    | '2D'
    | '3d'
    | 'addsub'
    | 'angle'
    | 'area'
    | 'chance'
    | 'dec'
    | 'equations'
    | 'frac'
    | 'indices'
    | 'length'
    | 'log'
    | 'mass'
    | 'measure_general'
    | 'money'
    | 'multdiv'
    | 'numTheory'
    | 'num_general'
    | 'operations'
    | 'patterns'
    | 'perc'
    | 'proofs'
    | 'rates'
    | 'stats'
    | 'temp'
    | 'time'
    | 'transform'
    | 'trig'
    | 'vol'
    | 'whole_nums';

/**
 * Create a local cache of the icons mapped to readable key names.
 */
let _cache: Record<TeacherAdminIcons, string>;

// eslint-disable-next-line no-undef
export function loadAllIconsToCache (module: __WebpackModuleApi.RequireContext) {
    if (_cache) {
        return _cache;
    }

    _cache = module.keys().reduce((
        currentCache: Record<string, string>,
        key: string
    ) => {
        // Derive the icon name from the module path and remove the prefix of the filename.
        const filenameWithoutExtension = key.match(/^.+\/([^.]+)\.svg$/)?.[1];
        const iconName = filenameWithoutExtension?.replace(/^icon_/, '');
        // storybook and the main webpack app have different loaders now for assets.
        const modulePath = module(key)?.default || module(key);

        if (typeof iconName !== 'string') {
            return currentCache;
        }

        return {
            ...currentCache,
            [iconName as TeacherAdminIcons]: modulePath
        };
    }, {});

    return _cache;
}

/**
 * Derive a icon from the require context cache by name.
 * Any svg image with the filename that matches /icon_.*.\.svg/ will be added.
 */
export function getTeacherAdminIcon (name: TeacherAdminIcons): string {
    if (isInJest()) {
        // require.context is not available in Jest.
        return '/assets/images/jest-mock-icon.svg';
    }

    loadAllIconsToCache(
        require.context('@3plearning/3p-teacheradmin-assets/assets/', true, /icon_.*.\.svg/)
    );

    assert(
        typeof _cache[name] !== 'undefined',
        `Unable to load icon ${name} from 3p-teacheradmin-assets`
    );

    return _cache[name];
}
